// src/services/index.js

// ====== CONFIG ======
const RAW_API = process.env.REACT_APP_API_URL || ""; // ex.: https://us-central1-<project>.cloudfunctions.net/api
const API = RAW_API.replace(/\/+$/, ""); // normaliza removendo barras finais

if (!RAW_API) {
  // eslint-disable-next-line no-console
  console.warn(
    "[services] REACT_APP_API_URL não definido. Defina no .env para apontar para suas Cloud Functions (/api)."
  );
}

export const PRICE_MENSAL = process.env.REACT_APP_STRIPE_PRICE_MENSAL; // deve começar com price_
export const PRICE_ANUAL = process.env.REACT_APP_STRIPE_PRICE_ANUAL; // deve começar com price_
export const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY; // pk_live_... (se usar Elements futuramente)

// Avisos gentis de envs
if (PRICE_MENSAL && PRICE_MENSAL.startsWith("prod_")) {
  // eslint-disable-next-line no-console
  console.warn(
    "[services] REACT_APP_STRIPE_PRICE_MENSAL parece ser um ID de produto (prod_...). Use o ID de PREÇO (price_...)."
  );
}
if (PRICE_ANUAL && PRICE_ANUAL.startsWith("prod_")) {
  // eslint-disable-next-line no-console
  console.warn(
    "[services] REACT_APP_STRIPE_PRICE_ANUAL parece ser um ID de produto (prod_...). Use o ID de PREÇO (price_...)."
  );
}

// ====== HELPERS ======
async function postJSON(path, payload) {
  const url = `${API}${path.startsWith("/") ? path : `/${path}`}`;

  let resp;
  try {
    resp = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload || {}),
    });
  } catch (networkErr) {
    // Ajuda a diagnosticar CORS/preflight/URL errada
    throw new Error(
      `Falha de rede ao chamar ${url}. Verifique CORS/URL das Functions. Detalhe: ${networkErr?.message || networkErr}`
    );
  }

  // Tenta parsear json mesmo em erro para extrair { error }
  let data = null;
  try {
    data = await resp.json();
  } catch (_) {
    data = null;
  }

  if (!resp.ok) {
    const msg = data?.error || `Falha na requisição: ${resp.status} ${resp.statusText}`;
    throw new Error(`${msg} (endpoint: ${url})`);
  }

  return data ?? {};
}

function redirect(url) {
  if (typeof window !== "undefined" && url) window.location.href = url;
}

// ====== PAGAMENTO (CHECKOUT) ======
/**
 * Cria sessão de checkout no Stripe (subscription) e redireciona o usuário.
 * @param {{ uid: string, priceId: string, successUrl?: string, cancelUrl?: string }} params
 */
export async function createCheckoutSession({ uid, priceId, successUrl, cancelUrl }) {
  if (!uid) throw new Error("UID obrigatório");
  if (!priceId) throw new Error("priceId obrigatório");
  if (priceId.startsWith("prod_")) {
    throw new Error("priceId inválido (recebi prod_...). Use o ID do PREÇO (price_...).");
  }

  const payload = {
    uid,
    priceId,
    successUrl: successUrl || `${window.location.origin}/assinatura/sucesso`,
    cancelUrl: cancelUrl || `${window.location.origin}/assinatura/cancelada`,
  };

  const { url } = await postJSON("/createCheckoutSession", payload);
  redirect(url);
  return url;
}

/**
 * Atalho: inicia assinatura escolhendo automaticamente o price pelo tipo.
 * @param {{ uid: string, tipo: "mensal"|"anual", successUrl?: string, cancelUrl?: string }} params
 */
export async function startSubscription({ uid, tipo, successUrl, cancelUrl }) {
  const priceId = getPriceId(tipo);
  return createCheckoutSession({ uid, priceId, successUrl, cancelUrl });
}

// ====== PORTAL DO CLIENTE (opcional) ======
/**
 * Abre o Billing Portal do Stripe para o cliente gerenciar pagamento/nota/cartão.
 * @param {{ uid: string, returnUrl?: string }} params
 */
export async function createPortalSession({ uid, returnUrl }) {
  if (!uid) throw new Error("UID obrigatório");
  const payload = { uid, returnUrl: returnUrl || `${window.location.origin}/minha-conta` };
  const { url } = await postJSON("/createPortalSession", payload);
  redirect(url);
  return url;
}

// ====== CANCELAMENTO (no app) ======
/**
 * Agenda cancelamento da assinatura no fim do ciclo (sem reembolso).
 * @param {string} subscriptionId - ID da assinatura no Stripe (ex.: sub_ABC...)
 * @returns {Promise<{ok:boolean, status:string, cancelAtPeriodEnd:boolean}>}
 */
export async function cancelAtPeriodEnd(subscriptionId) {
  if (!subscriptionId) throw new Error("subscriptionId obrigatório");
  return postJSON("/cancelAtPeriodEnd", { subscriptionId });
}

// ====== UTILIDADES ======
/**
 * Decide automaticamente o priceId com base no tipo ("mensal" | "anual")
 * @param {"mensal"|"anual"} tipo
 */
export function getPriceId(tipo) {
  if (tipo === "mensal") return PRICE_MENSAL;
  if (tipo === "anual") return PRICE_ANUAL;
  throw new Error("Tipo de plano inválido. Use 'mensal' ou 'anual'.");
}
